import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { initWebVitals } from './utils/webVitals'
import { registerSW } from 'virtual:pwa-register'
import { toast } from 'sonner'

// Lovable Preview fix: the manifest request can be redirected to lovable.dev/auth-bridge
// and fail with CORS, which triggers Lovable's "The app encountered an error" overlay.
// We only remove the manifest link in dev/preview; production keeps full PWA behavior.
if (!import.meta.env.PROD) {
  try {
    const host = window.location.hostname;
    const isLovablePreview = host.endsWith('lovableproject.com') || host.endsWith('lovable.app');
    if (isLovablePreview) {
      document.querySelector('link[rel="manifest"]')?.remove();
    }
  } catch {
    // ignore
  }
}

// App version - increment this on each deploy to force updates
const APP_VERSION = '2.4.0';
const VERSION_KEY = 'ai4me_app_version';

// Initialize Web Vitals monitoring for SEO performance
initWebVitals();

// Check for version mismatch and force reload
const checkVersionAndUpdate = () => {
  const storedVersion = localStorage.getItem(VERSION_KEY);
  
  if (storedVersion && storedVersion !== APP_VERSION) {
    console.log(`[PWA] Version mismatch: ${storedVersion} -> ${APP_VERSION}, forcing update...`);
    localStorage.setItem(VERSION_KEY, APP_VERSION);
    
    // Clear all caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    
    // Force reload
    window.location.reload();
    return true;
  }
  
  localStorage.setItem(VERSION_KEY, APP_VERSION);
  return false;
};

// PWA: ensure Android/iOS don't get stuck on an older cached version
if (import.meta.env.PROD) {
  // Check version first
  const needsReload = checkVersionAndUpdate();
  
  if (!needsReload) {
    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        // Auto-update immediately on mobile for better UX
        console.log('[PWA] New version detected, updating automatically...');
        
        // Show brief toast and update
        toast.success('Atualizando para nova versão...', {
          duration: 2000,
        });
        
        // Clear caches and update
        if ('caches' in window) {
          caches.keys().then(names => {
            names.forEach(name => caches.delete(name));
          });
        }
        
        // Small delay for toast visibility, then update
        setTimeout(() => {
          updateSW(true);
        }, 1500);
      },
      onOfflineReady() {
        console.log('[PWA] App ready for offline use');
      },
      onRegisteredSW(swUrl, registration) {
        console.log('[PWA] Service Worker registered:', swUrl);
        
        // Check for updates every 30 seconds while app is open
        setInterval(() => {
          registration?.update();
        }, 30 * 1000);
      },
      onRegisterError(error) {
        console.warn('[PWA] SW register error', error);
      },
    });
    
    // Also check for updates on visibility change (when user returns to app)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        navigator.serviceWorker?.ready.then(registration => {
          registration.update();
        });
      }
    });
  }
}

// Log version in console for debugging
console.log(`[AI4ME] Version ${APP_VERSION}`);

createRoot(document.getElementById("root")!).render(<App />);
